import { VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import App from '@/app/App.vue';
import { installI18n } from '@/app/i18n';
import { queryClient } from '@/app/query-client';
import { router, vueRouter } from '@/app/router';
import { authConfig } from '@/services/auth/auth-config';
import { startKioskHeartbeat } from '@/services/telemetry/kiosk-analytics';
import { initPostHog } from '@/services/telemetry/posthog';
import { sentryService } from '@/services/telemetry/sentry';
import domJSON from '@/shared/utils/dom-json';
import { track } from '@/shared/utils/track-telemetry';
import {
  deploymentVersion,
  reloadWithCacheBust,
} from './services/DeploymentVersion';

// Initialize auth config - extracts api_key from URL parameters (?api_key=xxx or ?key=xxx)
authConfig.init();
void initPostHog();

// Expose domJSON to global scope for console access
window.domJSON = domJSON;

// Create Vue app BEFORE Sentry init - Sentry's Vue integration requires the app instance
const app = createApp(App);

// Initialize Sentry with Vue app instance for proper Vue integration.
// Wrapped in try-catch: Sentry failure must never prevent the kiosk from booting.
// On a 24/7 kiosk, a working app without telemetry is infinitely better than no app.
try {
  sentryService.init({
    app: app,
    router: vueRouter,
  });

  // Start an explicit "kiosk-boot" Sentry session for errors that happen
  // between browser start and first user tap (config loading, kioskDesign init, etc.).
  // Without this, boot-time errors have no session boundary and get lumped into
  // the first user session or into an implicit Sentry session with no context.
  sentryService.setTag('kiosk_session_id', 'kiosk-boot');
  sentryService.startSentrySession();
} catch (e) {
  console.error(
    '[App] Sentry init failed, continuing without telemetry:',
    e.message
  );
}

// Global error handlers own crash reporting for uncaught errors and unhandled
// rejections. Sentry's built-in GlobalHandlers integration is DISABLED in
// init.ts (onerror + onunhandledrejection) because on WebKit (Chrome iOS /
// Safari) its legacy window.onerror hook receives no Error object and a bogus
// "undefined" filename for stack-overflow RangeErrors, producing stackless,
// unmappable events (KIOSK-FRONTEND-3N/10Q). The ErrorEvent /
// PromiseRejectionEvent DO carry the real thrown object with its stack, so we
// recapture it here and restore the true unhandled-crash mechanism.
//
// Guard against duplicate registration: on a 24/7 kiosk, HMR or module re-evaluation
// would stack anonymous listeners. Using a named function + a global flag ensures
// at most one handler per event type across all evaluations of this module.
if (!window.__kioskGlobalHandlersRegistered) {
  window.__kioskGlobalHandlersRegistered = true;

  window.addEventListener('unhandledrejection', (event) => {
    console.error('[Global] Unhandled promise rejection:', event.reason);
    // A null/undefined reason (Promise.reject() with no value — seen on iOS
    // during page tear-down) carries nothing actionable and would otherwise be
    // wrapped into a NonErrorCapture noise event; the old Sentry-built-in path
    // reported it as "…captured with value: undefined", which ignoreErrors
    // already drops. Match that by skipping it outright.
    if (event.reason != null) {
      sentryService.captureException(event.reason, {
        mechanism: { type: 'onunhandledrejection', handled: false },
      });
    }
  });

  window.addEventListener('error', (event) => {
    console.error('[Global] Uncaught error:', event.error);
    // event.error is null only for cross-origin "Script error." reports, which
    // carry no stack, file, or actionable message — skip them rather than emit
    // a second flavor of unmappable noise.
    if (event.error) {
      sentryService.captureException(event.error, {
        mechanism: { type: 'onerror', handled: false },
      });
    }
  });
}

// Vue error handler - chain with Sentry's built-in handler instead of replacing it.
// Sentry.init({app}) installs its own errorHandler via attachErrorHandler() that captures
// rich Vue context (componentName, lifecycleHook, component trace, propsData).
// If we replace it, all that metadata is lost. Instead, save it and delegate to it.
const sentryVueErrorHandler = app.config.errorHandler;
app.config.errorHandler = (err, instance, info) => {
  // Delegate to Sentry's Vue integration handler FIRST.
  // It captures component trace, lifecycle hook, and props -- richer than manual captureException.
  if (typeof sentryVueErrorHandler === 'function') {
    try {
      sentryVueErrorHandler(err, instance, info);
    } catch (_e) {
      // Sentry handler failure must not prevent our console logging
    }
  } else {
    // Fallback: Sentry handler was not installed (init failed or app not passed).
    // Use manual capture to still get basic error reporting.
    sentryService.captureException(err, {
      tags: { error_type: 'vue_error' },
      extra: {
        vueInfo: info,
        componentName: instance?.$options?.name || 'Unknown',
      },
    });
  }
  console.error('[Vue Error]', info, err);
  // Don't rethrow to prevent page issues on 24/7 kiosk
};

// Vue warning handler - forward to Sentry as breadcrumbs for 24/7 kiosk visibility.
// On deployed kiosks, console output is invisible. Vue warnings (invalid props, missing
// keys, deprecated usage) often signal real problems that surface as errors later.
// Capturing them as breadcrumbs ensures they appear in Sentry error context.
app.config.warnHandler = (msg, instance, trace) => {
  console.warn('[Vue Warning]', msg, trace);
  sentryService.addBreadcrumb(
    'vue.warning',
    msg,
    {
      componentName: instance?.$options?.name || 'Unknown',
      trace: trace?.slice(0, 500),
    },
    'warning'
  );
};

app.use(createPinia());
app.use(VueQueryPlugin, { queryClient });
installI18n(app);
app.use(vueRouter);

// Catch errors during route navigation (component setup(), guards, async loading).
// Without this, route-level errors only reach app.config.errorHandler which has
// no route-specific context. On a 24/7 kiosk, this prevents silent route failures.
vueRouter.onError((err) => {
  console.error('[Router] Navigation error:', err);
  sentryService.captureException(err, {
    tags: { error_type: 'router_navigation_error' },
    extra: { route: window.location.pathname },
  });
});

// Mount the app after the router resolves the initial navigation.
// CRITICAL: Both .then() and .catch() must mount the app. If isReady() rejects
// (e.g., navigation guard throws during initial route resolution), the kiosk
// must still render -- a visible error screen is recoverable, a blank screen is not.
//
// Timeout safety: if isReady() never settles (stuck navigation guard), mount
// after 5 seconds to prevent an indefinitely blank kiosk screen.
const MOUNT_TIMEOUT_MS = 5000;
let mounted = false;

function mountApp() {
  if (mounted) return;
  mounted = true;
  try {
    app.mount('#app');
  } catch (e) {
    // mount() itself threw (e.g., #app element missing, template compilation error).
    // Log prominently -- on a 24/7 kiosk this is a critical boot failure.
    console.error('[App] CRITICAL: Vue app failed to mount:', e);
    sentryService.captureException(e, {
      tags: { error_type: 'mount_failure', severity: 'critical' },
    });

    // CRITICAL: Inject fallback HTML so the kiosk never shows a white screen.
    // This is pure DOM manipulation — no Vue, no framework, no dependencies.
    const appEl = document.getElementById('app');
    if (appEl) {
      appEl.innerHTML = `
        <div style="position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#0a0a0a;color:#fff;font-family:system-ui,sans-serif;z-index:99999">
          <div style="text-align:center;padding:2rem">
            <h1 style="font-size:1.75rem;margin:0 0 .75rem">Kiosk failed to start</h1>
            <p style="opacity:.7;margin:0 0 1rem">Reloading automatically...</p>
          </div>
        </div>`;
    }
    // Auto-reload after 5 seconds — gives Sentry time to flush
    setTimeout(() => {
      try {
        sentryService.flush(2000);
      } catch (_) {
        /* best effort */
      }
      reloadWithCacheBust();
    }, 5000);
  }
}

// Heartbeat: prove to the watchdog (index.html) that Vue's JS runtime is alive.
// The watchdog reloads the page if this timestamp goes stale for >60s.
setInterval(() => {
  window.__kioskHeartbeat = Date.now();
}, 15_000);

// Deployment version polling: detect new builds and reload at safe moments.
// Wrapped in try-catch: version polling failure must never prevent the kiosk from working.
track('[App] Deployment version init failed:', () => {
  deploymentVersion.initialize();
});

startKioskHeartbeat(() => router.isStepPage);

vueRouter
  .isReady()
  .then(mountApp)
  .catch((err) => {
    console.error(
      '[App] Router initial navigation failed, mounting anyway:',
      err
    );
    sentryService.captureException(err, {
      tags: { error_type: 'router_init_failure' },
    });
    mountApp();
  });

// Fallback: if isReady() never resolves/rejects (stuck guard), force mount.
setTimeout(() => {
  if (!mounted) {
    console.error(
      '[App] Router isReady() did not settle within timeout, forcing mount'
    );
    sentryService.captureMessage('Router isReady() timeout - forced mount', {
      level: 'error',
      tags: { error_type: 'router_ready_timeout' },
    });
    mountApp();
  }
}, MOUNT_TIMEOUT_MS);
